import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { ChatBadgeProvider } from './context/ChatBadgeContext';
import { BadgeProvider } from './context/BadgeContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import HomePage from './components/pages/HomePage';
import CoursesPage from './components/pages/CoursesPage';
import ScreenersPage from './components/pages/ScreenersPage';
import ScreenerDetailPage from './components/pages/ScreenerDetailPage';
import CommunityPage from './components/pages/CommunityPage';
import PricingPage from './components/pages/PricingPage';
import AdminPage from './components/pages/admin/AdminPage';
import AdminTutorialsTab from './components/pages/admin/AdminTutorialsTab';
import AdminScreenersTab from './components/pages/admin/AdminScreenersTab';
import AdminPlansTab from './components/pages/admin/AdminPlansTab';
import AdminUsersTab from './components/pages/admin/AdminUsersTab';
import AdminChatTab from './components/pages/admin/AdminChatTab';
import AdminAnnouncementsTab from './components/pages/admin/AdminAnnouncementsTab';
import AnnouncementsPage from './components/pages/AnnouncementsPage';
import CourseTutorialPage from './components/pages/CourseTutorialPage';
import ChatSupportPage from './components/pages/ChatSupportPage';
import ChatWidget from './components/ChatWidget';
import NotFound from './components/pages/NotFound';
import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Pages that get the full-width marketing layout (no sidebar)
const MARKETING_PATHS = ['/', '/pricing'];

function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();

  const isMarketing = MARKETING_PATHS.includes(location.pathname);

  return (
    <div className="app-shell">
      <Navbar
        onMobileMenuClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        showHamburger={!isMarketing}
      />

      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileSidebarOpen(false)} />
      )}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <main
        className={
          isMarketing
            ? 'app-main app-main--marketing'
            : `app-main app-main--tool ${sidebarCollapsed ? 'app-main--collapsed' : ''}`
        }
      >
        <Routes>
          <Route path="/"            element={<HomePage />} />
          <Route path="/courses"           element={<CoursesPage />} />
          <Route path="/courses/:tutorialId" element={<CourseTutorialPage />} />
          <Route path="/screeners"      element={<ScreenersPage />} />
          <Route path="/screeners/:id"  element={<ScreenerDetailPage />} />
          <Route path="/community"   element={<CommunityPage />} />
          <Route path="/pricing"       element={<PricingPage />} />
          <Route path="/chat-support"    element={<ChatSupportPage />} />
          <Route path="/announcements"   element={<AnnouncementsPage />} />
          <Route path="/admin" element={<AdminPage />}>
            <Route index element={<Navigate to="/admin/tutorials" replace />} />
            <Route path="tutorials" element={<AdminTutorialsTab />} />
            <Route path="screeners" element={<AdminScreenersTab />} />
            <Route path="plans"     element={<AdminPlansTab />} />
            <Route path="users"     element={<AdminUsersTab />} />
            <Route path="chat"          element={<AdminChatTab />} />
            <Route path="announcements" element={<AdminAnnouncementsTab />} />
          </Route>
          <Route path="*"            element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <ChatBadgeProvider>
        <BadgeProvider>
        <AppLayout />
        <ChatWidget />
        <ToastContainer
          position="top-right"
          autoClose={3500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss={false}
          pauseOnHover
          theme="colored"
        />
        </BadgeProvider>
        </ChatBadgeProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
